import { Order } from '../../common/Order';
import { OrderPosition } from '../../common/OrderPosition';
import { OrderPositionStatusConstants } from '../../common/OrderPositionStatus';
import { Setting } from '../../common/Setting';

/**
 * The order receipt (#205): one narrow slip per order, listing its positions, for a receipt
 * printer. It is a „Bestellbeleg", not a fiscal receipt — no TSE, nothing under the KassenSichV —
 * and says so by its title.
 */

/** The Customer-scoped setting that holds the printer's paper width in whole centimetres. */
export const RECEIPT_WIDTH_SETTING = 'PosPrinterPageWidth';

/** The width when the customer has none stored — the backend's seeded default, an 80 mm roll. */
export const DEFAULT_RECEIPT_WIDTH_CM = 8;

/** What the settings form accepts, and therefore all a stored value can be. */
export const MIN_RECEIPT_WIDTH_CM = 1;
export const MAX_RECEIPT_WIDTH_CM = 30;

/** The mark across a receipt: a reprint of an order, or the sample from the settings page. */
export type ReceiptWatermark = 'copy' | 'test';

export interface ReceiptVatLine {
  rate: number;
  amount: number;
}

export interface ReceiptTotals {
  net: number;
  /** Already deducted from every amount above — the receipt names it, it does not subtract it. */
  discount: number;
  vat: ReceiptVatLine[];
  gross: number;
}

/** A width setting's value in centimetres, or null when it is not one the form would accept. */
export function parseReceiptWidth(value: string | null | undefined): number | null {
  if (value == null || !/^\d+$/.test(value.trim())) {
    return null;
  }
  const cm = Number(value.trim());
  return cm >= MIN_RECEIPT_WIDTH_CM && cm <= MAX_RECEIPT_WIDTH_CM ? cm : null;
}

/** The paper width the customer's settings rows ask for, or the default. */
export function receiptWidthCm(settings: Setting[] | null | undefined): number {
  const stored = settings?.find((s) => s.name === RECEIPT_WIDTH_SETTING)?.value;
  return parseReceiptWidth(stored) ?? DEFAULT_RECEIPT_WIDTH_CM;
}

/**
 * The positions a receipt lists: all but the cancelled ones, by position number.
 *
 * A cancelled position was refunded, and the backend leaves it out of the order's own totals
 * (`OrderCalculateAmountsCommand`), so listing it would put a sale on the slip that did not
 * happen. A position whose cancellation is only *requested* is still sold and is listed.
 */
export function receiptPositions(order: Order | null | undefined): OrderPosition[] {
  return (order?.orderPositions ?? [])
    .filter((p) => p.status !== OrderPositionStatusConstants.PositionStatusIdCancelled)
    .sort((a, b) => a.positionNumber - b.positionNumber);
}

/**
 * The receipt's totals, summed from the positions it lists.
 *
 * Never from the order's own `vatAmount`: checkout does not set it, so it reads 0 on every
 * order. The invoice sums the positions too, which is what keeps the two documents in step —
 * the VAT lines here add up to the invoice's single VAT total. Sums run in cents, so a column
 * of 19.99s does not end in …0000004.
 */
export function receiptTotals(positions: OrderPosition[]): ReceiptTotals {
  const vatCentsByRate = new Map<number, number>();
  for (const p of positions) {
    const rate = p.vatPercentage ?? 0;
    vatCentsByRate.set(rate, (vatCentsByRate.get(rate) ?? 0) + cents(p.vatAmount));
  }
  return {
    net: sumOf(positions, (p) => p.netPrice),
    discount: sumOf(positions, (p) => p.discount),
    // Highest rate first, as receipts usually read: 19 % above 7 %.
    vat: Array.from(vatCentsByRate, ([rate, amount]) => ({ rate, amount: amount / 100 })).sort(
      (a, b) => b.rate - a.rate,
    ),
    gross: sumOf(positions, (p) => p.grossPrice),
  };
}

/** The seller's name as the receipt heads it: the company, or the person when there is none. */
export function sellerName(order: Order): string {
  return order.creatorCustomerCompanyName || order.creatorCustomerFullName || '';
}

/** The customer's name on the receipt, chosen the same way. */
export function buyerName(order: Order): string {
  return order.buyerCustomerCompanyName || order.buyerCustomerFullName || '';
}

/**
 * The order the settings page prints as its test page: two lines at the two German rates, so a
 * test shows every part of a real receipt. Its words are translated; its numbers are not data.
 */
export function sampleReceiptOrder(): Order {
  const order = new Order();
  order.orderNumber = 1001;
  order.orderDateTime = new Date();
  order.currency = 'EUR';
  order.creatorCustomerCompanyName = $localize`:@@orderReceipt.sample.seller:Musterladen`;
  order.creatorCustomerStreet = $localize`:@@orderReceipt.sample.sellerStreet:Hauptstraße 1`;
  order.creatorCustomerZIP = '12345';
  order.creatorCustomerCity = $localize`:@@orderReceipt.sample.city:Musterstadt`;
  order.creatorCustomerPhone = '0123 456789';
  order.buyerCustomerNumber = 4711;
  order.buyerCustomerFullName = $localize`:@@orderReceipt.sample.buyer:Erika Mustermann`;
  order.buyerCustomerStreet = $localize`:@@orderReceipt.sample.buyerStreet:Gartenweg 2`;
  order.buyerCustomerZip = '12345';
  order.buyerCustomerCity = $localize`:@@orderReceipt.sample.city:Musterstadt`;
  order.orderPositions = [
    samplePosition(1, $localize`:@@orderReceipt.sample.articleA:Beispielartikel A`, 2, 11.9, 19),
    samplePosition(2, $localize`:@@orderReceipt.sample.articleB:Beispielartikel B`, 1, 5.35, 7),
  ];
  return order;
}

function samplePosition(
  positionNumber: number,
  name: string,
  quantity: number,
  unitGrossPrice: number,
  vatPercentage: number,
): OrderPosition {
  const position = new OrderPosition();
  const grossCents = cents(unitGrossPrice) * quantity;
  const netCents = Math.round(grossCents / (1 + vatPercentage / 100));
  position.positionNumber = positionNumber;
  position.skuName1 = name;
  position.quantity = quantity;
  position.unitGrossPrice = unitGrossPrice;
  position.grossPrice = grossCents / 100;
  position.netPrice = netCents / 100;
  position.vatPercentage = vatPercentage;
  position.vatAmount = (grossCents - netCents) / 100;
  position.discount = 0;
  position.status = OrderPositionStatusConstants.PositionStatusIdDefault;
  return position;
}

function cents(amount: number | null | undefined): number {
  return Math.round((amount ?? 0) * 100);
}

function sumOf(positions: OrderPosition[], amount: (p: OrderPosition) => number): number {
  return positions.reduce((total, p) => total + cents(amount(p)), 0) / 100;
}
